import type { AtualizarCifDto, CifListaResponse, CifResponse, CopiaCifsLoteResponse, CopiaCifsResponse, CopiarCifsDto, CopiarCifsLoteDto, CriarCifDto, GerarCifsDoMesDto, GerarCifsDoMesResponse, GradeCopiaCifsResponse, PreviaCopiaCifsResponse } from '@triade/contracts';
import { useConsulta, useMutacao } from './base';

/** Lancamentos de CIF da empresa (GET /cifs). Valor so vem com `custo.ver`; o rodape `somaValor` tambem. */
export function useCifs(q: { competencia?: string; q?: string } = {}) {
  return useConsulta<CifListaResponse>(['cifs'], '/cifs', { query: q });
}

/** Um lancamento de CIF (GET /cifs/:id). */
export function useCif(id: string | undefined) {
  return useConsulta<CifResponse>(['cifs', id], `/cifs/${id}`, { ativo: !!id });
}

/** Criacao (POST /cifs): `rateio.editar`. */
export function useCriarCif() {
  return useMutacao<CifResponse, CriarCifDto>({
    metodo: 'POST',
    caminho: () => '/cifs',
    corpo: (v) => v,
    invalidar: [['cifs'], ['rateios']],
  });
}

/** Alteracao (PATCH /cifs/:id): `rateio.editar`. */
export function useAtualizarCif() {
  return useMutacao<CifResponse, { id: string } & AtualizarCifDto>({
    metodo: 'PATCH',
    caminho: (v) => `/cifs/${v.id}`,
    corpo: ({ id: _id, ...resto }) => resto,
    invalidar: [['cifs'], ['rateios']],
  });
}

/** Exclusao (DELETE /cifs/:id): `rateio.editar`. */
export function useExcluirCif() {
  return useMutacao<void, string>({
    metodo: 'DELETE',
    caminho: (id) => `/cifs/${id}`,
    invalidar: [['cifs'], ['rateios']],
  });
}

/**
 * Previa de "Copiar de outro mes" (GET /cifs/copiar/previa): `rateio.ver`. Sem `habilitado` (origem invalida ou igual ao
 * destino) a consulta nao roda. Traz `destinoFechado`, as CIFs que serao criadas e as que serao puladas.
 */
export function usePreviaCopiaCifs(origem: string, destino: string, habilitado: boolean) {
  return useConsulta<PreviaCopiaCifsResponse>(['cifs', 'copiar-previa'], '/cifs/copiar/previa', {
    query: { origem, destino },
    ativo: habilitado,
  });
}

/** Copia os lancamentos de `origem` para `destino` (POST /cifs/copiar): `rateio.editar`. Invalida CIFs e rateios. */
export function useCopiarCifs() {
  return useMutacao<CopiaCifsResponse, CopiarCifsDto>({
    metodo: 'POST',
    caminho: () => '/cifs/copiar',
    corpo: (v) => v,
    invalidar: [['cifs'], ['rateios']],
  });
}

/** Gera os lancamentos do mes a partir dos itens de CIF ativos (POST /cifs/gerar-do-mes): `rateio.editar`. Valores zerados. */
export function useGerarCifsDoMes() {
  return useMutacao<GerarCifsDoMesResponse, GerarCifsDoMesDto>({
    metodo: 'POST',
    caminho: () => '/cifs/gerar-do-mes',
    corpo: (v) => v,
    invalidar: [['cifs'], ['rateios']],
  });
}

/**
 * Grade de "Copiar do mes" (GET /cifs/copiar/grade): TODAS as CIFs da origem, com `existeNoDestino` e `destinoFechado`.
 * Exige `rateio.ver` + `custo.ver` (a grade traz valores). Sem `habilitado` a consulta nao roda.
 */
export function useGradeCopiaCifs(origem: string, destino: string, habilitado: boolean) {
  return useConsulta<GradeCopiaCifsResponse>(['cifs', 'copiar-grade', origem, destino], '/cifs/copiar/grade', {
    query: { origem, destino },
    ativo: habilitado,
  });
}

/**
 * Copia em lote com a grade (POST /cifs/copiar/lote): grava so as linhas marcadas, numa transacao, com o valor e a
 * apropriacao editada de cada uma. `rateio.editar` + `custo.ver`. Destino fechado devolve 409 RATEIO_FECHADO.
 */
export function useCopiarCifsLote() {
  return useMutacao<CopiaCifsLoteResponse, CopiarCifsLoteDto>({
    metodo: 'POST',
    caminho: () => '/cifs/copiar/lote',
    corpo: (v) => v,
    invalidar: [['cifs'], ['rateios']],
  });
}
